/**
 * Customer Settings V2 — "Admin" section: account option toggles,
 * the staff-only feature list, integrations, and special
 * instructions.
 *
 * Contains:
 * - AdminSection (default export)
 */

import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import { Alert, Box, List as MuiList, Typography } from '@mui/material';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import {
  createCustomerSpecialInstruction,
  deleteCustomerSpecialInstruction,
  fetchCustomerSpecialInstruction,
  fetchCustomerSpecialInstructionsForCustomer,
  patchCustomerSpecialInstruction,
} from '@sophocles/entities/CustomerSpecialInstruction';
import { loadCustomerFeatureAssociations, loadFeatures } from '@sophocles/features/featureFlags/api';
import FeatureCard from '@sophocles/features/featureFlags/components/FeatureCard';
import { selectFeaturesByEntityType } from '@sophocles/features/featureFlags/slice';
import { selectTenantInfo } from '@sophocles/features/tenant/slice';
import {
  SettingsFieldRow,
  SettingsSection,
  SettingsSubsection,
  SettingsSwitchField,
  SpecialInstructionsList,
} from '@sophocles/views/settings/components';

import { adminOnlyChip, staffOnlyChip } from './chips';
import IntegrationList from './IntegrationList';

// Admin section.  Mirrors the legacy "Options" tab 1:1:
//
// 1. **Options** — six CustomerOptions flags. (Charting moved to the
//    LEGACY_CHARTING feature association — managed in Features.)
//    Writes flow through the consolidated PATCH (admin.options).
// 2. **Features** — FeatureCard tiles, staff-only marker.  Toggles
//    fire createFeatureAssociation / editFeatureAssociation
//    immediately (separate endpoint), same as Site/SiteGroup.
// 3. **Integrations** — IntegrationList when the API feature
//    is effectively enabled.  Read-only on this PATCH; the list
//    component owns its own CRUD.
// 4. **Instructions** — V2 SpecialInstructionsList.  CRUD runs
//    through the CustomerSpecialInstruction endpoints (separate
//    from the consolidated PATCH so the unified Save bar isn't
//    responsible for them, matching the SiteGroup pattern).
export default function AdminSection({ customerId, hasFeatures, hasIntegrations }) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const tenantInfo = useSelector(selectTenantInfo);
  const customerFeatures = useSelector(selectFeaturesByEntityType('customer'));
  const [instructions, setInstructions] = React.useState([]);

  React.useEffect(() => {
    dispatch(loadFeatures());
    if (customerId != null) {
      dispatch(loadCustomerFeatureAssociations(customerId));
    }
  }, [dispatch, customerId]);

  const reloadInstructions = React.useCallback(() => {
    if (customerId == null) return;
    dispatch(fetchCustomerSpecialInstructionsForCustomer(customerId)).then(res => {
      if (res?.error) return;
      setInstructions(res);
    });
  }, [dispatch, customerId]);

  React.useEffect(() => {
    reloadInstructions();
  }, [reloadInstructions]);

  const adminChip = adminOnlyChip(t);

  return (
    <SettingsSection
      id="settings-admin"
      icon={<AdminPanelSettingsIcon />}
      title={t('customerSettings.v2.admin', 'Admin')}
      description={t(
        'customerSettings.v2.adminDescription',
        'Customer-level options, available features, and integrations.',
      )}
      chip={adminChip}
    >
      <SettingsSubsection
        title={t('customerSettings.v2.adminOptions', 'Account Options')}
        chip={staffOnlyChip(t)}
        bordered
      >
        <SettingsFieldRow
          label={t('customerSettings.requirePoNumberForOrders', 'Require PO # for Orders')}
          description={t(
            'customerSettings.v2.requirePoDescription',
            'Force a purchase order number on every order placed for this customer.',
          )}
          controlWidth={80}
        >
          <SettingsSwitchField name="admin.options.requires_po_number_for_orders" hideLabel />
        </SettingsFieldRow>
        <SettingsFieldRow
          label={t('customerSettings.requireAuthNameForOrders', 'Require Auth Name for Orders')}
          description={t(
            'customerSettings.v2.requireAuthNameDescription',
            'Force an authorization name on every order placed for this customer.',
          )}
          controlWidth={80}
        >
          <SettingsSwitchField name="admin.options.requires_auth_name_for_orders" hideLabel />
        </SettingsFieldRow>
        <SettingsFieldRow
          label={t('customerSettings.isDigitalVendor', 'Is a Digital Vendor')}
          description={t(
            'customerSettings.v2.isDigitalVendorDescription',
            'Marks this customer as a digital-panel vendor for third-party flows.',
          )}
          controlWidth={80}
        >
          <SettingsSwitchField name="admin.options.sells_digital_panels" hideLabel />
        </SettingsFieldRow>
        <SettingsFieldRow
          label={t('customerSettings.webhookEnabled', 'Webhook Enabled')}
          description={t(
            'customerSettings.v2.webhookEnabledDescription',
            'Enable outbound webhooks for this customer.',
          )}
          controlWidth={80}
        >
          <SettingsSwitchField name="admin.options.webhook_enabled" hideLabel />
        </SettingsFieldRow>
        <SettingsFieldRow
          label={t('customerSettings.usesRelayMonitorByDefault', 'Uses Relay Monitor by Default')}
          description={t(
            'customerSettings.usesRelayMonitorByDefaultDescription',
            'When enabled, new sites will automatically have relay monitoring enabled by default',
          )}
          controlWidth={80}
        >
          <SettingsSwitchField name="admin.options.uses_relay_monitor_by_default" hideLabel />
        </SettingsFieldRow>
      </SettingsSubsection>

      {hasFeatures ? (
        <SettingsSubsection
          title={t('customerSettings.v2.features', 'Features')}
          description={t(
            'customerSettings.v2.featuresDescription',
            'Toggleable customer-level capabilities. Changes take effect immediately.',
          )}
          chip={staffOnlyChip(t)}
          bordered
          flush
        >
          {!customerFeatures || customerFeatures.length === 0 ? (
            <Box sx={{ px: 2, py: 1.5 }}>
              <Typography variant="body2" color="text.secondary">
                {t('customerSettings.v2.noFeatures', 'No features available for this customer.')}
              </Typography>
            </Box>
          ) : (
            <MuiList disablePadding sx={{ '& > li:last-of-type': { borderBottom: 0 } }}>
              {customerFeatures.map(feature => (
                <FeatureCard key={feature.key} feature={feature} entityType="customer" entityId={customerId} />
              ))}
            </MuiList>
          )}
        </SettingsSubsection>
      ) : null}

      {hasIntegrations ? (
        <SettingsSubsection
          title={t('integrations.integrations', 'Integrations')}
          description={t('integrations.settingsDescription')}
          bordered
          flush
        >
          <IntegrationList customerId={customerId} />
        </SettingsSubsection>
      ) : (
        <SettingsSubsection title={t('integrations.integrations', 'Integrations')} bordered>
          <Box sx={{ p: 2 }}>
            <Alert severity="info">
              {t('integrations.noApiFeatureMessage', { phoneNumber: tenantInfo?.contact_phone })}
            </Alert>
          </Box>
        </SettingsSubsection>
      )}

      <SettingsSubsection
        title={t('customerSettings.v2.instructions', 'Special Instructions')}
        description={t(
          'customerSettings.v2.instructionsDescription',
          'Notes shown to ODL staff when creating, processing, and invoicing RMAs and orders.',
        )}
        chip={staffOnlyChip(t)}
        bordered
      >
        <Box sx={{ py: 1.5 }}>
          <SpecialInstructionsList
            instructions={instructions}
            onCreate={values => dispatch(createCustomerSpecialInstruction({ ...values, customer: customerId }))}
            onUpdate={(id, values) => dispatch(patchCustomerSpecialInstruction(id, values))}
            onDelete={id => dispatch(deleteCustomerSpecialInstruction(id))}
            onFetch={id => dispatch(fetchCustomerSpecialInstruction(id))}
            onReload={reloadInstructions}
          />
        </Box>
      </SettingsSubsection>
    </SettingsSection>
  );
}
